import { useRef } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface Props {
  src: string;
  alt: string;
  /** Amplitude du parallax en pixels (déplacement total). Default: 40. */
  amplitude?: number;
  /** Classe CSS appliquée au wrapper. L'image fait 100% width/height. */
  className?: string;
  /** Classe CSS appliquée à l'image elle-même. Default: object-cover. */
  imgClassName?: string;
  /** Point de cadrage CSS (object-position). Default: 'center'. */
  objectPosition?: string;
  /** Loading lazy par défaut. Passer 'eager' pour le Hero (LCP). */
  loading?: 'eager' | 'lazy';
}

/**
 * Image avec effet parallax (translateY selon la position de scroll).
 * L'image est légèrement plus grande que son wrapper pour éviter les
 * bandes vides aux extrémités.
 *
 * Désactive le parallax sur mobile (< 768 px) et si l'utilisateur a
 * prefers-reduced-motion : rend une image standard `object-cover`.
 */
export default function ParallaxImage({
  src,
  alt,
  amplitude = 40,
  className,
  imgClassName,
  objectPosition = 'center',
  loading = 'lazy',
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(
    scrollYProgress,
    [0, 1],
    [-amplitude, amplitude]
  );

  // Mobile + reduced-motion : rend une image fixe object-cover, sans calcul
  // de scroll continu. Économise CPU/batterie et évite l'effet à peine
  // perceptible sur petit écran.
  if (isMobile || prefersReducedMotion) {
    return (
      <div className={cn('relative overflow-hidden', className)}>
        <img
          src={src}
          alt={alt}
          loading={loading}
          style={{ objectPosition }}
          className={cn('absolute inset-0 h-full w-full object-cover', imgClassName)}
        />
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={cn('relative overflow-hidden', className)}
    >
      <motion.img
        src={src}
        alt={alt}
        loading={loading}
        style={{
          y,
          objectPosition,
          height: `calc(100% + ${amplitude * 2}px)`,
          top: -amplitude,
          position: 'absolute',
          left: 0,
          right: 0,
        }}
        className={cn(
          'w-full object-cover',
          imgClassName
        )}
      />
    </div>
  );
}
